import { action } from "@ember/object";
import Service from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { ChartOptions, TimeScaleOptions } from "chart.js";
import dayjs from "dayjs";

export type Timeframe = "year" | "trimester" | "month";

export default class DashboardService extends Service {
  @tracked
  timeframe: Timeframe = "trimester";

  @tracked
  endDate: Date = this.today;

  @action
  addOneMonthToDate() {
    this.endDate = dayjs(this.endDate).add(6, "weeks").toDate();
  }

  @action
  substractOneMonthToDate() {
    this.endDate = dayjs(this.endDate).subtract(6, "weeks").toDate();
  }

  @action
  resetTime() {
    this.endDate = this.today;
  }

  get timeframeStartDate(): Date {
    const date = new Date(this.endDate);
    let weeksAgo;

    if (this.timeframe === "month") weeksAgo = 6;
    else if (this.timeframe === "trimester") weeksAgo = 14;
    else if (this.timeframe === "year") weeksAgo = 52; // 1 year == 52 weeks

    // Go back X weeks ago
    date.setDate(date.getDate() - weeksAgo * 7);

    // Go back at the start of the month if we are using the "year" timeframe
    if (this.timeframe === "year") date.setDate(1);
    return date;
  }

  // Return the end date, at the end of the day
  get timeframeEndDate(): Date {
    return dayjs(this.endDate).endOf("day")?.toDate();
  }

  get scaleXTimeUnit(): TimeScaleOptions["time"]["unit"] {
    switch (this.timeframe) {
      case "month":
        return "day";
      case "trimester":
        return "week";
      case "year":
      default:
        return "month";
    }
  }

  get today(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  get chartOptions(): ChartOptions<"line"> {
    return {
      clip: false, // See https://github.com/chartjs/Chart.js/issues/9372
      scales: {
        x: {
          type: "time",
          grid: { display: false },
          time: {
            displayFormats: {
              week: "DD MMM",
              month: "MMM YY",
              day: "DD MMM"
            },
            unit: this.scaleXTimeUnit
          },
          min: +this.timeframeStartDate,
          max: +this.timeframeEndDate,
          ticks: { maxTicksLimit: 8 }
        },
        y: { display: false }
      },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      layout: {
        padding: {
          top: 4,
          bottom: 4
        }
      }
    };
  }
}

declare module "@ember/service" {
  interface Registry {
    dashboard: DashboardService;
  }
}
